import { Stack } from '@mui/material';
import { useCallback, useMemo, useState } from 'react';

import { useFeedback } from '@/components/notifications/Feedback/useFeedback';
import { useCopyToClipboard } from '@/hooks/useCopyToClipboard';
import { useOptionalSelectedSuggestionEvidenceContext } from '@/modules/aiSuggestions/context/SelectedSuggestionEvidence.context';
import { useOptionalAnnotatedPDFViewerContext } from '@/modules/pdf/PDFViewer/AnnotatedPDFViewer/context/AnnotatedPDFViewer.context';

import { AskDocumentQuestion_MessageFragment } from '../../graphql/AskDocumentQuestion.generated';
import { convertFileParagraphsToAnnotations } from './DocumentChatAssistantMessageBubble.utils';
import { DocumentChatCitationFooter } from './DocumentChatCitationFooter';
import { DocumentChatMarkdown } from './DocumentChatMarkdown';

export interface DocumentChatAssistantMessageBubbleProps {
  message: AskDocumentQuestion_MessageFragment;
  enableCopyCitationPopover?: boolean;
  isStreaming?: boolean;
}

export function DocumentChatAssistantMessageBubble({
  message,
  enableCopyCitationPopover = false,
  isStreaming = false,
}: DocumentChatAssistantMessageBubbleProps) {
  const { showFeedback } = useFeedback();
  const [copyToClipboard] = useCopyToClipboard();
  const suggestionEvidenceContext =
    useOptionalSelectedSuggestionEvidenceContext();
  const pdfViewerContext = useOptionalAnnotatedPDFViewerContext();
  const [currentCitationIndex, setCurrentCitationIndex] = useState(0);
  const hasStreamingContent = isStreaming && message.content.trim().length > 0;

  const annotations = useMemo(
    () => convertFileParagraphsToAnnotations(message),
    [message]
  );

  const paragraphIds = useMemo(() => {
    if (!message.fileParagraphs) {
      return [];
    }

    return message.fileParagraphs.map((paragraph) => paragraph.id);
  }, [message.fileParagraphs]);

  const withCitationCopy = useMemo(() => {
    if (paragraphIds.length === 0) {
      return message.content;
    }

    const citation = `[citations=${JSON.stringify(paragraphIds)}]`;
    return `${message.content} ${citation}`;
  }, [message.content, paragraphIds]);

  const handleCopy = useCallback(async () => {
    copyToClipboard(
      message.content,
      () => {
        showFeedback('Copied!', { variant: 'success' });
      },
      () => {
        showFeedback('Failed to copy');
      }
    );
  }, [message.content, copyToClipboard, showFeedback]);

  const handleNavigateCitation = useCallback(
    (direction: number) => {
      const newIndex =
        (currentCitationIndex + direction + annotations.length) %
        annotations.length;
      setCurrentCitationIndex(newIndex);

      pdfViewerContext?.setSearchQuery('');

      suggestionEvidenceContext?.setDocumentPanelProps((prev) => ({
        ...prev,
        annotations,
        activeAnnotationIndex: newIndex,
        activeDocumentId: annotations[newIndex]?.documentID,
        searchQuery: '',
      }));

      pdfViewerContext?.setAnnotations(annotations);
      pdfViewerContext?.setActiveAnnotationIndex(newIndex);
      const activeDocumentId = annotations[newIndex]?.documentID;
      if (activeDocumentId) {
        pdfViewerContext?.setOverrideDocumentId(activeDocumentId);
      }
    },
    [
      currentCitationIndex,
      annotations,
      suggestionEvidenceContext,
      pdfViewerContext,
    ]
  );

  const handleLocateCitation = useCallback(() => {
    const currentAnnotations = convertFileParagraphsToAnnotations(message);
    const activeDocumentId =
      currentAnnotations[currentCitationIndex]?.documentID;

    pdfViewerContext?.setSearchQuery('');

    suggestionEvidenceContext?.setDocumentPanelProps(() => ({
      activeDocumentViewerTab: 'document',
      annotations: currentAnnotations,
      activeAnnotationIndex: currentCitationIndex,
      activeDocumentId,
      searchQuery: '',
      searchLoading: false,
    }));

    if (activeDocumentId) {
      pdfViewerContext?.setOverrideDocumentId(activeDocumentId);
    }
    pdfViewerContext?.setAnnotations(currentAnnotations);
    pdfViewerContext?.setActiveAnnotationIndex(currentCitationIndex);
  }, [
    currentCitationIndex,
    message,
    suggestionEvidenceContext,
    pdfViewerContext,
  ]);

  return (
    <Stack
      sx={{
        minHeight: hasStreamingContent ? 200 : undefined,
      }}
    >
      <DocumentChatMarkdown markdown={message.content} />

      {!isStreaming && annotations.length > 0 && (
        <DocumentChatCitationFooter
          currentIndex={currentCitationIndex}
          totalCount={annotations.length}
          onNavigate={handleNavigateCitation}
          onCopy={handleCopy}
          onLocate={handleLocateCitation}
          searchResponse={message.content}
          withCitationCopy={withCitationCopy}
          enableCopyCitationPopover={enableCopyCitationPopover}
        />
      )}
    </Stack>
  );
}
